import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import io from 'socket.io-client'
import { v4 as uuidv4 } from 'uuid'
import { message } from 'antd'
import { apiBaseURL, socketURL } from '../config'
import BindingWechat  from './binding'

export default function Chat() {
  const { oid } = useParams()
  const [messages, setMessages] = useState([])
  const [enlargedImage, setEnlargedImage] = useState(null)
  const [senderId, setSenderId] = useState(localStorage.getItem('userId'))
  const [receiver, setReceiver] = useState({})
  const [showBindPrompt, setShowBindPrompt] = useState(false)
  const [socket, setSocket] = useState(null)
  const [showWelcomeMsg, setShowWelcomeMsg] = useState(false)
  const [isBindingWechatVisible, setIsBindingWechatVisible] = useState(false)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (!senderId || senderId === 'null') {
      setSenderId(uuidv4())
    } else {
      axios.post('/c/kf_c/chat_history/get', {
        identity: senderId,
        contact_id: oid,
      })
        .then(function (response) {
          if (response.errcode === 0) {
            setMessages(response.data.records)
          } else {
            message.warning(response.msg)
          }
        })
        .catch(function (error) {
          console.error('请求失败，错误信息为：', error)
        })
    }
    const isBind = localStorage.getItem('isBind')
    if (isBind === '0') {
      setShowBindPrompt(true)
    }
  }, [senderId, oid])

  useEffect(() => {
    axios.post('/c/kf_c/kefu/detail/get', {
      account_oid: oid,
    })
      .then(function (response) {
        if (response.errcode === 0) {
          setReceiver(response.data)

          const hasSeenWelcomeMsg = localStorage.getItem(`hasSeenWelcomeMsg_${oid}`)
          if (!hasSeenWelcomeMsg && response.data.welcome_msg) {
            setShowWelcomeMsg(true) // 显示欢迎语
            localStorage.setItem(`hasSeenWelcomeMsg_${oid}`, 'true') // 标记为已显示
          }
        } else {
          message.warning(response.msg)
        }
      })
      .catch(function (error) {
        console.error('请求失败，错误信息为：', error)
      })
  }, [oid, senderId])

  useEffect(() => {
    const socketConnection = io(socketURL, {
      query: {
        identity: senderId
      },
    })
    setSocket(socketConnection)

    socketConnection.on("connect", () => {
      console.log("✅ 客户端已成功连接")
    })

    socketConnection.on("connect_error", (err) => {
      console.error("❌ 连接失败:", err.message)
    })

    socketConnection.on('json', (newMessage) => {
      setMessages((prevMessages) => [...prevMessages, { ...newMessage, sender: 'received' }])
    })

    return () => {
      if (socketConnection) {
        socketConnection.disconnect()
      }
    }
  }, [senderId])

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
    }
  }, [messages, showWelcomeMsg])

  const handleSendMessage = (content, type = 'text') => {
    if (!content.trim() && type === 'text') return
    const newMessage = {
      content: content,
      sender: 'sent',
      senderId: senderId,
      senderRole: 'student',
      receiverId: receiver.oid,
      receiverRole: 'teacher',
      type,
      time: new Date().toISOString(),
    }
    setMessages((prevMessages) => [...prevMessages, newMessage])

    if (socket) {
      socket.emit('json', newMessage)
    }
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    const formData = new FormData()
    formData.append('image', file)

    if (file) {
      axios.post(`${apiBaseURL}/common/image/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then(function (response) {
          if (response.errcode === 0) {
            handleSendMessage(response.data.image_url, 'image')
          } else {
            message.warning(response.data.msg)
          }
        })
        .catch(function (error) {
          console.error('请求失败，错误信息为：', error)
        })
    }
  }

  const handleImageClick = (imageSrc) => {
    setEnlargedImage(imageSrc)
  }

  const handleCloseEnlargedImage = () => {
    setEnlargedImage(null)
  }

  const formatMessageTime = (timestamp) => {
    if (!timestamp) {
      return '未知时间'
    }

    const now = new Date()
    const messageDate = new Date(timestamp)

    if (isNaN(messageDate.getTime())) {
      return '未知时间'
    }

    const isToday = now.toDateString() === messageDate.toDateString()
    const isYesterday = new Date(now.setDate(now.getDate() - 1)).toDateString() === messageDate.toDateString()
    const isThisYear = now.getFullYear() === messageDate.getFullYear()

    if (isToday) {
      return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (isYesterday) {
      return `昨天 ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    } else if (isThisYear) {
      return `${messageDate.getMonth() + 1}-${messageDate.getDate()} ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    } else {
      return `${messageDate.getFullYear()}-${messageDate.getMonth() + 1}-${messageDate.getDate()} ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    }
  }

  return (
    <div className="chat-page">
      <div className="chat-window">
        <div className="chat-header">
          <img src={receiver.avatar || '/images/avatar.png'} alt={receiver.name} />
          <span>{receiver.group_name}｜{receiver.nickname}</span>
        </div>

        <div className="chat-messages">
          {showWelcomeMsg && (
            <div className="message received">
              <span>{receiver.welcome_msg}</span>
            </div>
          )}

          {/* {showBindPrompt && (
            <div className="chat-bindPrompt" onClick={() => { setIsBindingWechatVisible(true) }}>
              <p>
                关注
                <span>微信公众号</span>
                ，以便于当您处于离线状态时，可以接收老师回复的消息
              </p>
            </div>
          )} */}

          {messages.map((message, index) => {
            const currentTime = new Date(message.time).getTime()
            const prevTime = index > 0 ? new Date(messages[index - 1].time).getTime() : null
            const showTime = !prevTime || (currentTime - prevTime) > 5 * 60 * 1000

            return (
              <React.Fragment key={index}>
                {showTime && (
                  <div className="message-time">
                    {formatMessageTime(message.time)}
                  </div>
                )}

                {message.type === 'text' ? (
                  <div className={`message ${message.sender}`}>
                    <span>{message.content}</span>
                  </div>
                ) : (
                  <img
                    src={message.content}
                    alt="sent"
                    className={`chat-image ${message.sender === 'sent' ? 'sent-image' : 'received-image'}`}
                    onClick={() => handleImageClick(message.content)}
                    onLoad={() => {
                      if (messagesEndRef.current) {
                        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
                      }
                    }}
                  />
                )}
              </React.Fragment>
            )
          })}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input">
          <div className="chat-input-upload">
            <label htmlFor="file-upload">
              <img src="/images/upload.png" alt="上传图片" />
              <input
                id="file-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
            </label>
          </div>
          
          <textarea
            className="chat-input-textarea desktop"
            placeholder="输入"
            enterKeyHint="send"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSendMessage(e.target.value)
                e.target.value = ''
              }
            }}
          />
          <input
            className="chat-input-textarea mobile"
            placeholder="输入"
            enterKeyHint="send"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSendMessage(e.target.value)
                e.target.value = ''
              }
            }}
          />

          <div className="chat-input-send">
            <button
              onClick={() => {
                const input = document.querySelector('.chat-input-textarea')
                handleSendMessage(input.value)
                input.value = ''
              }}
            >
              发送
            </button>
          </div>
        </div>
      </div>

      {enlargedImage && (
        <div className="image-modal" onClick={handleCloseEnlargedImage}>
          <img src={enlargedImage} alt="enlarged" className="enlarged-image" />
        </div>
      )}

      {isBindingWechatVisible && <BindingWechat
        isOpen={isBindingWechatVisible}
        isCancel={() => { setIsBindingWechatVisible(false) }}
      />}
    </div>
  )
}